import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { IoClose } from 'react-icons/io5';
import html2canvas from 'html2canvas';
import { RichTextEditor } from './RichTextEditor';
import RmbMeetingPoster from '../PosterDesign/Meeting/RmbMeetingPoster';
import {
  FaCalendarAlt, FaHistory, FaCheckCircle, FaAward, FaFilePdf,
  FaImages, FaInstagram, FaCrown, FaMicrophone, FaTrashAlt,
  FaMapMarkerAlt, FaBuilding, FaUserTie, FaUsers, FaInfoCircle,
  FaPaintBrush, FaDownload, FaEye, FaSyncAlt, FaFileImage
} from 'react-icons/fa';

interface EventModalProps {
  eventData?: any | null;
  selectedDate?: Date | null;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Required fields per event type ────────────────────────────────────────────
const EVENT_TYPE_LABELS: Record<string, { color: string; icon: string; label: string }> = {
  'General Meeting': { color: '#8B5CF6', icon: '📋', label: 'General Meeting' },
  'Meeting':         { color: '#3B82F6', icon: '🤝', label: 'Meeting' },
  'Field Visit':     { color: '#10B981', icon: '🏭', label: 'Field Visit' },
  'Installation':    { color: '#F59E0B', icon: '👑', label: 'Installation Ceremony' },
  'Webinar':         { color: '#6366F1', icon: '💻', label: 'Webinar' },
  'Social':          { color: '#EC4899', icon: '🎉', label: 'Social Event' },
  'Other':           { color: '#6B7280', icon: '📌', label: 'Other Event' },
};

const EventModal: React.FC<EventModalProps> = ({ eventData, selectedDate, onClose, onSuccess }) => {
  const [eventTimeline, setEventTimeline] = useState<'future' | 'past'>('future');
  const [members, setMembers] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    event_name:        '',
    location:          '',
    chapter:           'RMB Jaipur',
    event_type:        'General Meeting',
    event_date:        '',
    description:       '',
    rmbian_of_meeting: '',
    presentation_1:    '',   // Speaker / Chief Guest / Field Visit Member / Pres-1
    presentation_2:    '',   // Company / Education Slot / Chief Guest Org / Pres-2
    speaker_designation: '', // Webinar only
    instagram_reel:    '',
    amount:            '1,200',
    dress_code:        'Formal',
    meeting_meal:      'Meeting followed by Breakfast',
  });

  const [thumbnail,   setThumbnail]   = useState<File | null>(null);
  const [photosList,  setPhotosList]  = useState<File[]>([]);
  const [momPdf,      setMomPdf]      = useState<File | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // ─── Meeting Poster State ─────────────────────────────────────────────────────
  const [usePosterThumbnail, setUsePosterThumbnail] = useState<boolean>(true);
  const [posterCustomTitle1, setPosterCustomTitle1] = useState<string>('');
  const [posterCustomTitle2, setPosterCustomTitle2] = useState<string>('');
  const [showPosterOptions, setShowPosterOptions] = useState<boolean>(false);
  const [isGeneratingPoster, setIsGeneratingPoster] = useState<boolean>(false);
  const posterRef = useRef<HTMLDivElement>(null);

  // Extract meeting number e.g. "127th" from title
  const extractMeetingNum = (title: string): string => {
    if (!title) return '127th';
    const match = title.match(/(\d+(?:st|nd|rd|th)?)/i);
    if (match) {
      const val = match[1];
      if (/^\d+$/.test(val)) {
        const n = parseInt(val, 10);
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return val;
    }
    return '127th';
  };

  // Compile live props for RmbMeetingPoster
  const getPosterData = () => {
    // Title lines
    const meetingNum = extractMeetingNum(formData.event_name);
    const title0 = posterCustomTitle1.trim() || `RMB JAIPUR'S ${meetingNum}`;
    const title1 = posterCustomTitle2.trim() || 'REGULAR MEETING';

    // Date
    let pDate = { day: '26th SEPT.', year: '2026', weekday: 'SATURDAY' };
    let pTime = ['8:00 AM', 'onwards'];
    if (formData.event_date) {
      try {
        const raw = formData.event_date;
        const d = new Date(raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw);
        if (!isNaN(d.getTime())) {
          const day = d.getDate();
          const s = ['th', 'st', 'nd', 'rd'];
          const v = day % 100;
          const dayOrd = day + (s[(v - 20) % 10] || s[v] || s[0]);
          const months = ['JAN.', 'FEB.', 'MAR.', 'APR.', 'MAY', 'JUNE', 'JULY', 'AUG.', 'SEPT.', 'OCT.', 'NOV.', 'DEC.'];
          const weekdays = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          pDate = {
            day: `${dayOrd} ${months[d.getMonth()]}`,
            year: String(d.getFullYear()),
            weekday: weekdays[d.getDay()]
          };
          let hours = d.getHours();
          const mins = d.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          pTime = [`${hours}:${mins < 10 ? '0' + mins : mins} ${ampm}`, 'onwards'];
        }
      } catch {}
    }

    // Venue
    let venue0 = 'The Fern Jaipur, Series by';
    let venue1 = 'Marriott, Tonk Road';
    if (formData.location) {
      const parts = formData.location.split(/,|\n/);
      venue0 = parts[0]?.trim() || formData.location;
      venue1 = parts.slice(1).join(', ').trim() || '';
    }

    // Slots
    const m1 = members.find(m => (m.name || '').trim().toLowerCase() === (formData.presentation_1 || '').trim().toLowerCase());
    let photo1 = '';
    if (m1?.profile) {
      photo1 = m1.profile.startsWith('http')
        ? m1.profile
        : `https://rmbbackend.rmbjaipur.com/${m1.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`;
    }
    const slot0 = {
      heading: ['BUSINESS', 'PRESENTATION'] as [string, string],
      name: formData.presentation_1 ? (formData.presentation_1.toUpperCase().startsWith('RMBIAN') ? formData.presentation_1.toUpperCase() : `RMBIAN: ${formData.presentation_1.toUpperCase()}`) : 'RMBIAN SPEAKER',
      company: m1?.company_name || 'RMB Member',
      photo: photo1
    };

    const m2 = members.find(m => (m.name || '').trim().toLowerCase() === (formData.presentation_2 || '').trim().toLowerCase());
    let photo2 = '';
    if (m2?.profile) {
      photo2 = m2.profile.startsWith('http')
        ? m2.profile
        : `https://rmbbackend.rmbjaipur.com/${m2.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`;
    }
    const slot1 = {
      heading: ['EDUCATION', 'Slot'] as [string, string],
      name: formData.presentation_2 ? (formData.presentation_2.toUpperCase().startsWith('RMBIAN') ? formData.presentation_2.toUpperCase() : `RMBIAN: ${formData.presentation_2.toUpperCase()}`) : 'RMBIAN PRESENTER',
      company: m2?.company_name || 'RMB Member',
      photo: photo2
    };

    return {
      title: [title0, title1] as [string, string],
      date: pDate,
      time: pTime as [string, string],
      venue: [venue0, venue1] as [string, string],
      slots: [slot0, slot1],
      dressCode: formData.dress_code || 'Formal',
      charges: `REGISTRATION CHARGES RS. ${formData.amount || '1,200'}/- FOR VISITORS`,
      note: `( ${formData.meeting_meal || 'Meeting followed by Breakfast'} )`,
    };
  };

  const handleDownloadPoster = async () => {
    if (!posterRef.current) return;
    try {
      setIsGeneratingPoster(true);
      const canvas = await html2canvas(posterRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      const link = document.createElement('a');
      link.download = `${(formData.event_name || 'RMB_Meeting_Poster').replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error generating poster download:', err);
      alert('Could not download poster. Please try again.');
    } finally {
      setIsGeneratingPoster(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setPhotosList(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleRemovePhoto = (index: number) =>
    setPhotosList(prev => prev.filter((_, i) => i !== index));

  const formatLocalDatetime = (rawDate: any) => {
    if (!rawDate) return '';
    let str = String(rawDate).trim();
    if (str.includes(' ') && !str.includes('T')) str = str.replace(' ', 'T');
    const d = new Date(str);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // ─── Load members ─────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setMembers(list.filter((u: any) =>
          u.role !== 'super-admin' && u.delete !== 1 &&
          u.name && u.name.trim().toLowerCase() !== 'admin'
        ));
      })
      .catch(err => console.error('Error fetching members in EventModal:', err));
  }, []);

  // ─── Populate form when editing ───────────────────────────────────────────────
  useEffect(() => {
    if (eventData) {
      const rawDate     = eventData.event_date || eventData.start_date || eventData.date;
      const formattedDate = formatLocalDatetime(rawDate);

      let timeline: 'future' | 'past' = 'future';
      if (rawDate) {
        let s = String(rawDate).trim();
        if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          if (d < todayStart) timeline = 'past';
        }
      }

      setEventTimeline(timeline);
      setFormData({
        event_name:          eventData.heading || eventData.title || eventData.description || '',
        location:            eventData.location || eventData.venue || '',
        chapter:             eventData.chapter || 'RMB Jaipur',
        event_type:          eventData.event_type || 'General Meeting',
        event_date:          formattedDate,
        description:         eventData.description || eventData.content || '',
        rmbian_of_meeting:   String(eventData.rmbian_of_meeting || eventData.RMBiansOfMeeting || ''),
        presentation_1:      eventData.presentation_1 || '',
        presentation_2:      eventData.presentation_2 || '',
        speaker_designation: eventData.speaker_designation || '',
        instagram_reel:      eventData.instagram_reel || '',
        amount:              eventData.amount || '1,200',
        dress_code:          eventData.dress_code || 'Formal',
        meeting_meal:        eventData.meeting_meal || 'Meeting followed by Breakfast',
      });

    } else if (selectedDate) {
      let targetDate = new Date(selectedDate);
      if (targetDate.getHours() === 0 && targetDate.getMinutes() === 0) targetDate.setHours(10, 0, 0, 0);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      setEventTimeline(targetDate < todayStart ? 'past' : 'future');
      setFormData(prev => ({ ...prev, event_date: formatLocalDatetime(targetDate) }));

    } else {
      // Default: tomorrow 10:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      setFormData(prev => ({ ...prev, event_date: formatLocalDatetime(tomorrow) }));
    }
  }, [eventData, selectedDate]);

  // ─── Timeline toggle ──────────────────────────────────────────────────────────
  const handleTimelineChange = (timeline: 'future' | 'past') => {
    setEventTimeline(timeline);
    if (eventData) return;
    const now = new Date();
    if (timeline === 'past') {
      const curr = formData.event_date ? new Date(formData.event_date) : now;
      if (curr >= now) {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(18, 0, 0, 0);
        setFormData(prev => ({ ...prev, event_date: yesterday.toISOString().slice(0, 16) }));
      }
    } else {
      const curr = formData.event_date ? new Date(formData.event_date) : now;
      if (curr < now) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        setFormData(prev => ({ ...prev, event_date: tomorrow.toISOString().slice(0, 16) }));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ─── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!eventData?.id) return;
    if (!window.confirm(`Delete "${formData.event_name || 'this event'}"?`)) return;
    setLoading(true);
    try {
      const rawId = String(eventData.id).replace('news-', '');
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/events/${rawId}`).catch(() => {});
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/news/${rawId}`).catch(() => {});
      setLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError('Failed to delete event.');
      setLoading(false);
    }
  };

  // ─── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      data.append('heading',     formData.event_name || 'Chapter Event');
      data.append('title',       formData.event_name || 'Chapter Event');
      data.append('location',    formData.location);
      data.append('chapter',     formData.chapter);
      data.append('event_type',  formData.event_type);
      data.append('event_date',  formData.event_date);
      data.append('description', formData.description || formData.event_name);

      // Presentation fields per type
      let pres1 = formData.presentation_1;
      let pres2 = formData.presentation_2;
      if (formData.event_type === 'Social') { pres1 = ''; pres2 = ''; }
      if (formData.event_type === 'Webinar' && formData.speaker_designation && pres1 && !pres1.includes(formData.speaker_designation)) {
        pres1 = `${pres1} (${formData.speaker_designation})`;
      }
      data.append('presentation_1', pres1);
      data.append('presentation_2', pres2);
      data.append('amount', formData.amount || '1,200');
      data.append('dress_code', formData.dress_code || 'Formal');
      data.append('meeting_meal', formData.meeting_meal || 'Meeting followed by Breakfast');

      // Past-event-only fields
      data.append('rmbian_of_meeting', eventTimeline === 'past' ? formData.rmbian_of_meeting : '');
      if (eventTimeline === 'past' && formData.instagram_reel) {
        data.append('instagram_reel', formData.instagram_reel);
      }

      // Files
      let finalThumbnail = thumbnail;
      const isGeneralMeeting = formData.event_type === 'General Meeting' || formData.event_type === 'Meeting';

      if (isGeneralMeeting && usePosterThumbnail && posterRef.current) {
        try {
          setIsGeneratingPoster(true);
          const canvas = await html2canvas(posterRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
          });
          const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png', 0.95));
          if (blob) {
            finalThumbnail = new File([blob], `meeting_poster_${Date.now()}.png`, { type: 'image/png' });
          }
        } catch (canvasErr) {
          console.warn('Canvas poster capture notice:', canvasErr);
        } finally {
          setIsGeneratingPoster(false);
        }
      }

      if (finalThumbnail) {
        data.append('thumbnail', finalThumbnail);
        data.append('image', finalThumbnail);
      }
      photosList.forEach(f => { data.append('photos', f); data.append('images', f); });
      if (momPdf && eventTimeline === 'past') data.append('mom_pdf', momPdf);

      if (eventData?.id) {
        if (eventData.photographs) data.append('existing_photographs', eventData.photographs);
        if (eventData.mom_pdf)     data.append('existing_mom_pdf',     eventData.mom_pdf);
        const rawId = String(eventData.id).replace('news-', '');
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/events/${rawId}`, data);
      } else {
        // --- Log payload and alert before sending to API ---
        debugger;
        const eventSummary = {
          event_name: formData.event_name,
          event_date: formData.event_date,
          location: formData.location,
          presentation_1: pres1,
          presentation_2: pres2,
          amount: formData.amount || '1,200',
          dress_code: formData.dress_code || 'Formal',
          meeting_meal: formData.meeting_meal || 'Meeting followed by Breakfast'
        };
        console.log('📢 Creating Event & Sending WhatsApp Meeting Template Payload:', eventSummary);

        const targetInput = window.prompt(
          `📢 Event Creation — WhatsApp Campaign Options:\n\n` +
          `• Title: ${formData.event_name || 'Chapter Event'}\n` +
          `• Date & Time: ${formData.event_date}\n` +
          `• Venue: ${formData.location || 'The Fern'}\n\n` +
          `Options:\n` +
          `1. Enter phone number (e.g. 917301020313) to send a TEST first\n` +
          `2. Type "ALL" to broadcast to all members & Admin\n` +
          `3. Leave BLANK to save event WITHOUT sending WhatsApp\n` +
          `4. Click Cancel to abort`,
          '917301020313'
        );

        if (targetInput === null) {
          setLoading(false);
          return;
        }

        if (targetInput.trim()) {
          const isBroadcastAll = targetInput.trim().toUpperCase() === 'ALL';
          if (isBroadcastAll) {
            data.append('test_number', '');
          } else {
            data.append('test_number', targetInput.trim());
          }
        } else {
          data.append('skip_whatsapp', 'true');
        }

        const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/events', data);
        alert(`🎉 ${res.data?.message || 'Event created successfully!'}`);
      }

      setLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save event. Please check inputs.');
      setLoading(false);
    }
  };

  // ─── Event-type-specific required fields (FUTURE only) ───────────────────────
  const renderFutureTypeFields = () => {
    const et = formData.event_type;

    if (et === 'General Meeting' || et === 'Meeting') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50/70 p-4 rounded-xl border border-purple-200">
          <div className="md:col-span-2">
            <span className="block text-[11px] font-black text-purple-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center text-white text-[9px] font-black">1</span>
              Speaker / Presenter Details
            </span>
          </div>
          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">
              💼 Presentation 1 — Business Presenter <span className="text-rose-500">*</span>
            </label>
            <select
              name="presentation_1"
              value={formData.presentation_1}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
            >
              <option value="">-- Select Active Member --</option>
              {formData.presentation_1 && !members.some(m => (m.name || '').trim() === formData.presentation_1) && (
                <option value={formData.presentation_1}>{formData.presentation_1}</option>
              )}
              {members.map((m: any) => (
                <option key={m.id} value={(m.name || '').trim()}>
                  {(m.name || '').trim()} ({m.company_name || 'RMB Member'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">
              🎓 Presentation 2 / Education Slot
            </label>
            <select
              name="presentation_2"
              value={formData.presentation_2}
              onChange={handleChange}
              className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
            >
              <option value="">-- Select Active Member (Optional) --</option>
              {formData.presentation_2 && !members.some(m => (m.name || '').trim() === formData.presentation_2) && (
                <option value={formData.presentation_2}>{formData.presentation_2}</option>
              )}
              {members.map((m: any) => (
                <option key={m.id} value={(m.name || '').trim()}>
                  {(m.name || '').trim()} ({m.company_name || 'RMB Member'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">
              💰 Meeting Fee / Amount (₹)
            </label>
            <input
              type="text"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="e.g. 1,200"
              className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">
              👔 Dress Code
            </label>
            <input
              type="text"
              name="dress_code"
              value={formData.dress_code}
              onChange={handleChange}
              placeholder="e.g. Formal"
              className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-purple-900 mb-1">
              🍽️ Meeting Followed By / Meal Details
            </label>
            <input
              type="text"
              name="meeting_meal"
              value={formData.meeting_meal}
              onChange={handleChange}
              placeholder="e.g. Meeting followed by Breakfast"
              className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
        </div>
      );
    }

    if (et === 'Field Visit') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-emerald-50/70 p-4 rounded-xl border border-emerald-200">
          <div className="md:col-span-2">
            <span className="block text-[11px] font-black text-emerald-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FaUsers className="text-emerald-600" /> Field Visit Host Details
            </span>
          </div>
          <div>
            <label className="block text-xs font-bold text-emerald-900 mb-1">
              📍 Field Visit Member Name <span className="text-rose-500">*</span>
            </label>
            <select
              name="presentation_1"
              value={formData.presentation_1}
              required
              onChange={(e) => {
                const selectedName = e.target.value;
                const userObj = members.find((m: any) => (m.name || '').trim() === selectedName);
                setFormData(prev => ({
                  ...prev,
                  presentation_1: selectedName,
                  presentation_2: userObj?.company_name || prev.presentation_2
                }));
              }}
              className="w-full p-2.5 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-bold bg-white"
            >
              <option value="">-- Select Field Visit Member --</option>
              {formData.presentation_1 && !members.some(m => (m.name || '').trim() === formData.presentation_1) && (
                <option value={formData.presentation_1}>{formData.presentation_1}</option>
              )}
              {members.map((m: any) => (
                <option key={m.id} value={(m.name || '').trim()}>
                  {(m.name || '').trim()} ({m.company_name || 'RMB Member'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-emerald-900 mb-1">
              🏢 Business Name / Category / Company <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="presentation_2"
              placeholder="e.g. Aadvik Tech Services (IT Solutions)"
              value={formData.presentation_2}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
        </div>
      );
    }

    if (et === 'Installation') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-amber-50/70 p-4 rounded-xl border border-amber-300">
          <div className="md:col-span-2">
            <span className="block text-[11px] font-black text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FaCrown className="text-amber-500" /> Chief Guest Details
            </span>
          </div>
          <div>
            <label className="block text-xs font-bold text-amber-900 mb-1">
              <FaCrown className="inline text-amber-500 text-sm mr-1" /> Chief Guest Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="presentation_1"
              placeholder="e.g. Rtn. Sunil Kumar Gupta (District Governor)"
              value={formData.presentation_1}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-amber-900 mb-1">
              🏢 Chief Guest Company / Organization <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="presentation_2"
              placeholder="e.g. Rotary District 3054 / Apex Group"
              value={formData.presentation_2}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
        </div>
      );
    }

    if (et === 'Webinar') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-indigo-50/70 p-4 rounded-xl border border-indigo-200">
          <div className="md:col-span-3">
            <span className="block text-[11px] font-black text-indigo-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FaMicrophone className="text-indigo-600" /> Webinar Speaker Details
            </span>
          </div>
          <div>
            <label className="block text-xs font-bold text-indigo-900 mb-1">
              <FaMicrophone className="inline text-indigo-600 text-xs mr-1" /> Speaker Name <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="presentation_1"
              placeholder="e.g. Dr. Rajesh Sharma"
              value={formData.presentation_1}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-indigo-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-indigo-900 mb-1">
              🏢 Speaker Company <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="presentation_2"
              placeholder="e.g. Global Tech Solutions"
              value={formData.presentation_2}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-indigo-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-indigo-900 mb-1">
              🎓 Speaker Designation / Role <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="speaker_designation"
              placeholder="e.g. Managing Director & CEO"
              value={formData.speaker_designation}
              onChange={handleChange}
              required
              className="w-full p-2.5 border border-indigo-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-semibold bg-white"
            />
          </div>
        </div>
      );
    }

    if (et === 'Social') {
      return (
        <div className="bg-pink-50/60 p-3.5 rounded-xl border border-pink-200 text-xs text-pink-800 font-semibold flex items-center gap-2">
          <span className="text-xl">🎉</span>
          <span>Social events do not require presentation or speaker details — only the event thumbnail and description are needed.</span>
        </div>
      );
    }

    return null;
  };

  // ─── Determine if date is in future (for validation hint) ────────────────────
  const isFutureEvent = eventTimeline === 'future';
  const typeInfo = EVENT_TYPE_LABELS[formData.event_type] || EVENT_TYPE_LABELS['Other'];

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/75 z-50 p-3 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto relative border border-slate-200">

        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between rounded-t-2xl">
          <div>
            <span className="text-[10px] font-black uppercase text-amber-600 tracking-wider block">Admin Event Management</span>
            <h2 className="text-xl font-black text-slate-800">
              {eventData ? '✏️ Edit / Manage Event' : '➕ Create New Chapter Event'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-800 p-1.5 rounded-full hover:bg-slate-100 transition mt-0.5 cursor-pointer"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="px-5 pb-6 pt-5 space-y-5">
          {/* ── ERROR ──────────────────────────────────────────────────────────── */}
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm font-semibold flex items-center gap-2">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* ── TIMELINE SELECTOR ─────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <label className="block text-xs font-black uppercase text-slate-500 tracking-wider">
              Event Timeline Category
            </label>
            <div className="bg-slate-100 p-1.5 rounded-xl flex gap-2 border border-slate-200">
              <button
                type="button"
                onClick={() => handleTimelineChange('future')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-black text-xs transition flex items-center justify-center gap-2 cursor-pointer ${
                  isFutureEvent
                    ? 'bg-[#02256D] text-[#FDBC12] shadow-md'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                }`}
              >
                <FaCalendarAlt className="text-sm" />
                🗓️ Future / Upcoming Event
              </button>
              <button
                type="button"
                onClick={() => handleTimelineChange('past')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-black text-xs transition flex items-center justify-center gap-2 cursor-pointer ${
                  !isFutureEvent
                    ? 'bg-[#0A2451] text-[#FDBC12] shadow-md'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                }`}
              >
                <FaHistory className="text-sm" />
                📜 Past Event / Highlights
              </button>
            </div>
            <p className="text-[11px] text-slate-500 font-medium px-1">
              {isFutureEvent
                ? '✨ Upcoming events appear in the "Upcoming Events & General Meetings" calendar section.'
                : '📜 Past events appear in the "Latest News & Past Event Highlights" section with optional photos, MoM PDF, and Instagram reel.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* ── EVENT TYPE ──────────────────────────────────────────────────── */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Event Type <span className="text-rose-500">*</span>
              </label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
                required
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold bg-white"
              >
                <option value="General Meeting">📋 General Meeting</option>
                <option value="Meeting">🤝 Meeting</option>
                <option value="Field Visit">🏭 Field Visit</option>
                <option value="Installation">👑 Installation Ceremony</option>
                <option value="Webinar">💻 Webinar</option>
                <option value="Social">🎉 Social Event</option>
                <option value="Other">📌 Other</option>
              </select>
              {/* Type pill indicator */}
              <div className="mt-1.5 flex items-center gap-1.5">
                <span
                  className="px-2.5 py-0.5 rounded-full text-[10px] font-black text-white"
                  style={{ backgroundColor: typeInfo.color }}
                >
                  {typeInfo.icon} {typeInfo.label}
                </span>
                {isFutureEvent && (
                  <span className="text-[10px] text-slate-500 font-medium">
                    Fields below are required for this event type
                  </span>
                )}
              </div>
            </div>

            {/* ── COMMON CORE FIELDS ──────────────────────────────────────────── */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                📌 Event Title / Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="event_name"
                placeholder={
                  formData.event_type === 'General Meeting' ? 'e.g. General Meeting #127 — RMB Jaipur' :
                  formData.event_type === 'Field Visit'     ? 'e.g. Field Visit to Aadvik Tech Services' :
                  formData.event_type === 'Installation'    ? 'e.g. 127th Joint Installation Ceremony' :
                  formData.event_type === 'Webinar'         ? 'e.g. Digital Marketing Masterclass Webinar' :
                  formData.event_type === 'Social'          ? 'e.g. Diwali Networking Social Evening' :
                  'e.g. Chapter Event Title'
                }
                value={formData.event_name}
                onChange={handleChange}
                required
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold text-[#02256D]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  {isFutureEvent ? '🗓️ Scheduled Event Date & Time' : '📅 Past Event Date & Time'}
                  <span className="text-rose-500"> *</span>
                </label>
                <input
                  type="datetime-local"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  required
                  className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold"
                />
                <p className="text-[10px] text-slate-400 mt-0.5 font-medium px-0.5">
                  {isFutureEvent
                    ? '⚠️ Only the event date (not today\'s date) will be shown publicly.'
                    : '📜 This is the date the event was actually conducted.'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaMapMarkerAlt className="inline text-rose-500 text-xs mr-1" /> Location / Venue <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  placeholder="e.g. Hotel Rajputana Sheraton, Jaipur"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaBuilding className="inline text-blue-600 text-xs mr-1" /> Chapter Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="chapter"
                  placeholder="e.g. RMB Jaipur"
                  value={formData.chapter}
                  onChange={handleChange}
                  required
                  className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                />
              </div>
            </div>
            {renderFutureTypeFields()}

            {/* ── DESCRIPTION (RICH TEXT WYSIWYG EDITOR) ────────────────────────────────── */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                <FaInfoCircle className="inline text-blue-500 text-xs mr-1" />
                Event Description / Agenda (Formatted Text) <span className="text-rose-500">*</span>
              </label>
              <RichTextEditor
                value={formData.description}
                onChange={(htmlValue) => setFormData(prev => ({ ...prev, description: htmlValue }))}
                placeholder={
                  formData.event_type === 'General Meeting' ? 'Describe the meeting agenda, topics to be discussed, networking highlights...' :
                  formData.event_type === 'Field Visit'     ? 'Describe the purpose of the field visit, what will be explored, business insights...' :
                  formData.event_type === 'Installation'    ? 'Describe the installation ceremony agenda, incoming board, achievements...' :
                  formData.event_type === 'Webinar'         ? 'Describe the webinar topic, key takeaways, target audience...' :
                  formData.event_type === 'Social'          ? 'Describe the social event theme, activities, dress code...' :
                  'Enter event agenda, highlights, or description...'
                }
              />
            </div>

            {/* ── EVENT THUMBNAIL (Default RmbMeetingPoster for General Meetings, or Custom Upload) ── */}
            {formData.event_type === 'General Meeting' || formData.event_type === 'Meeting' ? (
              <div className="bg-gradient-to-br from-amber-50/90 via-orange-50/50 to-purple-50/60 p-4 sm:p-5 rounded-2xl border-2 border-amber-300 shadow-sm space-y-3.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-amber-200/80 pb-3">
                  <div>
                    <span className="text-[10px] font-black uppercase text-amber-700 tracking-wider flex items-center gap-1.5">
                      <FaPaintBrush className="text-amber-600" /> Default Meeting Thumbnail & WhatsApp Flyer
                    </span>
                    <h3 className="text-sm font-black text-slate-800">
                      RMB Meeting Invitation Poster (Auto-Generated)
                    </h3>
                  </div>

                  {/* Mode switcher tabs */}
                  <div className="flex items-center gap-1.5 bg-white/80 p-1 rounded-xl border border-amber-200 shadow-xs self-start sm:self-auto">
                    <button
                      type="button"
                      onClick={() => setUsePosterThumbnail(true)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition flex items-center gap-1.5 cursor-pointer ${
                        usePosterThumbnail
                          ? 'bg-[#02256D] text-[#FDBC12] shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <FaPaintBrush size={11} />
                      🎨 Default Poster Flyer
                    </button>
                    <button
                      type="button"
                      onClick={() => setUsePosterThumbnail(false)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                        !usePosterThumbnail
                          ? 'bg-[#02256D] text-[#FDBC12] shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <FaFileImage size={11} />
                      📁 Upload Custom
                    </button>
                  </div>
                </div>

                {usePosterThumbnail ? (
                  <div className="space-y-3.5">
                    <div className="bg-amber-100/70 border border-amber-300/80 text-amber-900 px-3.5 py-2.5 rounded-xl text-xs flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-base">✨</span>
                        <span className="font-semibold text-[11px] leading-tight">
                          By default, this official flyer is dynamically generated from your meeting details (agenda, venue, date/time, presenter, fee & meal), saved as the event thumbnail, and dispatched via WhatsApp.
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleDownloadPoster}
                        disabled={isGeneratingPoster}
                        className="shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-black rounded-lg text-[11px] shadow-xs transition flex items-center gap-1.5 cursor-pointer"
                        title="Download high-resolution poster PNG"
                      >
                        <FaDownload size={11} />
                        {isGeneratingPoster ? 'Generating...' : 'Download PNG'}
                      </button>
                    </div>

                    {/* Quick collapsible for typography / title override */}
                    <div className="bg-white/70 p-3 rounded-xl border border-amber-200/70 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                          ✏️ Customize Poster Title Lines (Optional Override)
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowPosterOptions(!showPosterOptions)}
                          className="text-[11px] font-bold text-amber-700 hover:text-amber-900 cursor-pointer"
                        >
                          {showPosterOptions ? '▲ Hide Options' : '▼ Show Options'}
                        </button>
                      </div>

                      {showPosterOptions && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-0.5">Title Line 1 (e.g. RMB JAIPUR'S 127th)</label>
                            <input
                              type="text"
                              value={posterCustomTitle1}
                              onChange={(e) => setPosterCustomTitle1(e.target.value)}
                              placeholder={`Auto: RMB JAIPUR'S ${extractMeetingNum(formData.event_name)}`}
                              className="w-full p-2 border border-slate-300 rounded-lg text-xs font-bold bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-0.5">Title Line 2 (e.g. REGULAR MEETING)</label>
                            <input
                              type="text"
                              value={posterCustomTitle2}
                              onChange={(e) => setPosterCustomTitle2(e.target.value)}
                              placeholder="Auto: REGULAR MEETING"
                              className="w-full p-2 border border-slate-300 rounded-lg text-xs font-bold bg-white"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Live Preview Container */}
                    <div className="flex flex-col items-center justify-center p-3 sm:p-4 bg-slate-900/90 rounded-2xl border border-slate-700 shadow-inner">
                      <div className="w-full flex items-center justify-between text-xs text-slate-300 font-bold mb-2.5 px-1">
                        <span className="flex items-center gap-1.5 text-amber-400">
                          <FaEye size={12} /> Live Poster Flyer Preview (3:4 Grid)
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          Updates live as you type
                        </span>
                      </div>

                      <div className="w-full max-w-[380px] rounded-xl overflow-hidden shadow-2xl transition-all">
                        <RmbMeetingPoster ref={posterRef} {...getPosterData()} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 bg-white/80 p-3.5 rounded-xl border border-amber-200">
                    <label className="block text-xs font-bold text-slate-700">
                      Upload Custom Banner Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setThumbnail(e.target.files ? e.target.files[0] : null)}
                      className="w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                    />
                    {thumbnail && (
                      <div className="flex items-center gap-2 mt-1">
                        <img
                          src={URL.createObjectURL(thumbnail)}
                          alt="Thumbnail preview"
                          className="h-16 w-24 object-cover rounded-lg border border-amber-300 shadow"
                        />
                        <div className="text-xs text-amber-800 font-semibold">
                          <p>✅ {thumbnail.name}</p>
                          <p className="text-[10px] text-slate-500">{(thumbnail.size / 1024).toFixed(0)} KB</p>
                        </div>
                      </div>
                    )}
                    {eventData && (eventData.photographs || eventData.img) && !thumbnail && (
                      <p className="text-[10px] text-amber-700 font-medium">
                        ℹ️ Existing thumbnail will be kept. Upload a new image to replace it.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Non-General Meeting Thumbnail (Field Visit, Webinar, Social, etc.) */
              <div className="bg-amber-50/60 p-4 rounded-xl border border-amber-200 space-y-1.5">
                <label className="block text-xs font-bold text-amber-900 flex items-center gap-1.5">
                  <FaImages className="text-amber-600 text-sm" />
                  📷 Event Thumbnail / Banner Image <span className="text-rose-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnail(e.target.files ? e.target.files[0] : null)}
                  className="w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                />
                {thumbnail && (
                  <div className="flex items-center gap-2 mt-1">
                    <img
                      src={URL.createObjectURL(thumbnail)}
                      alt="Thumbnail preview"
                      className="h-16 w-24 object-cover rounded-lg border border-amber-300 shadow"
                    />
                    <div className="text-xs text-amber-800 font-semibold">
                      <p>✅ {thumbnail.name}</p>
                      <p className="text-[10px] text-slate-500">{(thumbnail.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                )}
                {eventData && (eventData.photographs || eventData.img) && !thumbnail && (
                  <p className="text-[10px] text-amber-700 font-medium">
                    ℹ️ Existing thumbnail will be kept. Upload a new image to replace it.
                  </p>
                )}
                <p className="text-[10px] text-amber-700 font-medium">Cover photo shown on event cards and gallery.</p>
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {/* ── PAST-EVENT-ONLY SECTION ─────────────────────────────────────── */}
            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {!isFutureEvent && (
              <div className="border-2 border-dashed border-slate-300 rounded-2xl p-4 space-y-4 bg-slate-50">
                <div className="flex items-center gap-2 text-slate-600">
                  <FaHistory className="text-amber-500" />
                  <span className="text-xs font-black uppercase tracking-wider text-slate-500">Past Event — Optional Media & Details</span>
                </div>
                <p className="text-[11px] text-slate-500 font-medium -mt-2">
                  All fields below are optional. Fill them to enrich the past event highlight card.
                </p>

                {/* RMBian of the Meeting */}
                <div className="bg-amber-50/70 p-3.5 rounded-xl border border-amber-200 space-y-1">
                  <label className="block text-xs font-bold text-amber-900 flex items-center gap-1.5">
                    <FaAward className="text-amber-600 text-sm" /> 🏆 RMBian of the Meeting (Honored Member) — Optional
                  </label>
                  <select
                    name="rmbian_of_meeting"
                    value={formData.rmbian_of_meeting}
                    onChange={handleChange}
                    className="w-full p-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold bg-white text-slate-800"
                  >
                    <option value="">-- Select RMBian of the Meeting (Optional) --</option>
                    {formData.rmbian_of_meeting && !members.some(m => String(m.id) === String(formData.rmbian_of_meeting)) && (
                      <option value={formData.rmbian_of_meeting}>Member #{formData.rmbian_of_meeting}</option>
                    )}
                    {members.map((m: any) => (
                      <option key={m.id} value={String(m.id)}>
                        {(m.name || '').trim()} ({m.company_name || 'RMB Member'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Event Photographs (multiple) */}
                <div className="bg-blue-50/60 p-3.5 rounded-xl border border-blue-200 space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-bold text-blue-900 flex items-center gap-1.5">
                      <FaImages className="text-blue-600 text-sm" /> 🖼️ Event Photographs — Optional (Select Multiple)
                    </label>
                    {photosList.length > 0 && (
                      <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                        {photosList.length} Selected
                      </span>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                  />
                  <p className="text-[10px] text-slate-500 font-medium">
                    ✨ Hold Ctrl or Shift in the file picker to select multiple photos at once.
                  </p>
                  {photosList.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-2 border-t border-blue-200 max-h-40 overflow-y-auto">
                      {photosList.map((file, idx) => (
                        <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-300 bg-white h-16 flex items-center justify-center shadow-xs">
                          <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(idx)}
                            className="absolute top-0.5 right-0.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-black shadow transition"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* MoM PDF */}
                <div className="bg-rose-50/60 p-3.5 rounded-xl border border-rose-200 space-y-1">
                  <label className="block text-xs font-bold text-rose-900 flex items-center gap-1.5">
                    <FaFilePdf className="text-rose-600" /> Minutes of Meeting (MoM) PDF — Optional
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setMomPdf(e.target.files ? e.target.files[0] : null)}
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-rose-100 file:text-rose-800 hover:file:bg-rose-200 cursor-pointer"
                  />
                  {momPdf && (
                    <p className="text-[10px] text-rose-800 font-semibold">✅ {momPdf.name}</p>
                  )}
                  {eventData?.mom_pdf && !momPdf && (
                    <p className="text-[10px] text-rose-700 font-medium">ℹ️ Existing MoM PDF will be kept.</p>
                  )}
                  <p className="text-[10px] text-slate-500">Upload the official minutes of meeting document.</p>
                </div>

                {/* Instagram Reel */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <FaInstagram className="text-pink-600 text-sm" /> Instagram Reel Link — Optional
                  </label>
                  <input
                    type="url"
                    name="instagram_reel"
                    placeholder="e.g. https://www.instagram.com/reel/C..."
                    value={formData.instagram_reel}
                    onChange={handleChange}
                    className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none text-sm font-medium"
                  />
                </div>
              </div>
            )}

            {/* ── ACTION BUTTONS ────────────────────────────────────────────────── */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
              <div>
                {eventData && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-xl transition text-xs flex items-center gap-1.5 cursor-pointer border border-rose-200"
                  >
                    <FaTrashAlt /> Delete Event
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-xl transition text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-[#02256D] hover:bg-blue-900 text-[#FDBC12] font-black rounded-xl shadow-lg transition disabled:opacity-50 text-xs flex items-center gap-2 cursor-pointer"
                >
                  <FaCheckCircle />
                  {loading ? 'Saving...' : eventData ? 'Update Event' : `Save ${isFutureEvent ? 'Future' : 'Past'} Event`}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
